import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

// ============================================================
// Modal de texto propio (reemplaza window.prompt del navegador)
// Uso: const valor = await promptDialog({ title: 'Motivo' });
//      -> string (puede ser '') al aceptar, null al cancelar
// Con `options` muestra un select con esas opciones + "Otro"
// (que habilita el input libre).
// ============================================================

const OTRO = '__otro__';

export type PromptOpts = {
  title?: string;
  description?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  options?: string[];
};

let _openPrompt: ((o: PromptOpts) => Promise<string | null>) | null = null;

// API imperativa: `const v = await promptDialog({...}); if (v === null) return;`
export function promptDialog(o: PromptOpts | string): Promise<string | null> {
  const opts: PromptOpts = typeof o === 'string' ? { title: o } : o;
  if (_openPrompt) return _openPrompt(opts);
  // Fallback por si el host no está montado
  return Promise.resolve(window.prompt(opts.title || '', opts.defaultValue || ''));
}

export const PromptDialogHost: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<PromptOpts>({});
  const [value, setValue] = useState('');
  const [selected, setSelected] = useState('');
  const resolverRef = useRef<((v: string | null) => void) | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    _openPrompt = (o) =>
      new Promise<string | null>((resolve) => {
        setOpts(o);
        setValue(o.defaultValue || '');
        setSelected('');
        resolverRef.current = resolve;
        setOpen(true);
        if (!o.options?.length) setTimeout(() => inputRef.current?.focus(), 50);
      });
    return () => {
      _openPrompt = null;
    };
  }, []);

  const finish = (v: string | null) => {
    resolverRef.current?.(v);
    resolverRef.current = null;
    setOpen(false);
  };

  const hasOptions = !!opts.options?.length;
  const showInput = !hasOptions || selected === OTRO;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(null); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{opts.title || 'Ingresar valor'}</DialogTitle>
          {opts.description ? <DialogDescription>{opts.description}</DialogDescription> : null}
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            finish(hasOptions && selected !== OTRO ? selected : value);
          }}
          className="space-y-3"
        >
          {hasOptions && (
            <Select
              value={selected}
              onValueChange={(v) => {
                setSelected(v);
                if (v === OTRO) setTimeout(() => inputRef.current?.focus(), 50);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Elegí un motivo..." />
              </SelectTrigger>
              <SelectContent>
                {opts.options!.map((op) => (
                  <SelectItem key={op} value={op}>{op}</SelectItem>
                ))}
                <SelectItem value={OTRO}>Otro...</SelectItem>
              </SelectContent>
            </Select>
          )}
          {showInput && (
            <Input
              ref={inputRef}
              value={value}
              placeholder={hasOptions ? 'Escribí el motivo...' : (opts.placeholder || '')}
              onChange={(e) => setValue(e.target.value)}
            />
          )}
          <DialogFooter className="gap-2 sm:gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => finish(null)}>
              {opts.cancelText || 'Cancelar'}
            </Button>
            <Button type="submit">
              {opts.confirmText || 'Aceptar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PromptDialogHost;
